/* global axios */
import CacheEnabledApiClient from './CacheEnabledApiClient';

class Inboxes extends CacheEnabledApiClient {
  constructor() {
    super('inboxes', { accountScoped: true });
  }

  // eslint-disable-next-line class-methods-use-this
  get cacheModelName() {
    return 'inbox';
  }

  getCampaigns(inboxId) {
    return axios.get(`${this.url}/${inboxId}/campaigns`);
  }

  deleteInboxAvatar(inboxId) {
    return axios.delete(`${this.url}/${inboxId}/avatar`);
  }

  getAgentBot(inboxId) {
    return axios.get(`${this.url}/${inboxId}/agent_bot`);
  }

  setAgentBot(inboxId, botId) {
    return axios.post(`${this.url}/${inboxId}/set_agent_bot`, {
      agent_bot: botId,
    });
  }

  syncTemplates(inboxId) {
    return axios.post(`${this.url}/${inboxId}/sync_templates`);
  }

  getMessageTemplates(inboxId, params = {}, config = {}) {
    return axios.get(`${this.url}/${inboxId}/message_templates`, {
      ...config,
      params,
    });
  }

  updateWhatsappBusinessManagementToken(inboxId, businessManagementToken) {
    return axios.put(
      `${this.url}/${inboxId}/whatsapp_business_management_token`,
      {
        business_management_token: businessManagementToken,
      }
    );
  }

  getWhatsAppTemplates(inboxId) {
    return axios.get(`${this.url}/${inboxId}/whatsapp_templates`);
  }

  createWhatsAppTemplate(inboxId, template) {
    return axios.post(`${this.url}/${inboxId}/whatsapp_templates`, {
      template,
    });
  }

  updateWhatsAppTemplate(inboxId, templateId, template) {
    return axios.patch(`${this.url}/${inboxId}/whatsapp_templates`, {
      template_id: templateId,
      template,
    });
  }

  deleteWhatsAppTemplate(inboxId, params) {
    return axios.delete(`${this.url}/${inboxId}/whatsapp_templates`, {
      data: params,
    });
  }

  getWhatsAppFlows(inboxId) {
    return axios.get(`${this.url}/${inboxId}/whatsapp_flows`);
  }

  getWhatsAppCommerce(inboxId) {
    return axios.get(`${this.url}/${inboxId}/whatsapp_commerce`);
  }

  updateWhatsAppCommerce(inboxId, settings) {
    return axios.patch(`${this.url}/${inboxId}/whatsapp_commerce`, settings);
  }

  getWhatsAppCatalogProducts(inboxId, catalogId) {
    return axios.get(`${this.url}/${inboxId}/whatsapp_commerce/products`, {
      params: { catalog_id: catalogId },
    });
  }

  createWhatsAppCatalog(inboxId, catalog) {
    return axios.post(`${this.url}/${inboxId}/whatsapp_commerce/catalogs`, {
      catalog,
    });
  }

  updateWhatsAppCatalog(inboxId, catalogId, catalog) {
    return axios.patch(
      `${this.url}/${inboxId}/whatsapp_commerce/catalogs/${catalogId}`,
      {
        catalog,
      }
    );
  }

  deleteWhatsAppCatalog(inboxId, catalogId) {
    return axios.delete(
      `${this.url}/${inboxId}/whatsapp_commerce/catalogs/${catalogId}`
    );
  }

  createWhatsAppCatalogProduct(inboxId, catalogId, product) {
    return axios.post(`${this.url}/${inboxId}/whatsapp_commerce/products`, {
      catalog_id: catalogId,
      product,
    });
  }

  updateWhatsAppCatalogProduct(inboxId, catalogId, productId, product) {
    return axios.patch(
      `${this.url}/${inboxId}/whatsapp_commerce/products/${productId}`,
      {
        catalog_id: catalogId,
        product,
      }
    );
  }

  deleteWhatsAppCatalogProduct(inboxId, catalogId, productId) {
    return axios.delete(
      `${this.url}/${inboxId}/whatsapp_commerce/products/${productId}`,
      {
        data: { catalog_id: catalogId },
      }
    );
  }

  createWhatsAppFlow(inboxId, flow) {
    return axios.post(`${this.url}/${inboxId}/whatsapp_flows`, {
      flow,
    });
  }

  updateWhatsAppFlow(inboxId, flowId, flow) {
    return axios.patch(`${this.url}/${inboxId}/whatsapp_flows/${flowId}`, {
      flow,
    });
  }

  publishWhatsAppFlow(inboxId, flowId) {
    return axios.post(`${this.url}/${inboxId}/whatsapp_flows/${flowId}/publish`);
  }

  previewWhatsAppFlow(inboxId, flowId) {
    return axios.get(`${this.url}/${inboxId}/whatsapp_flows/${flowId}/preview`);
  }

  deprecateWhatsAppFlow(inboxId, flowId) {
    return axios.post(`${this.url}/${inboxId}/whatsapp_flows/${flowId}/deprecate`);
  }

  deleteWhatsAppFlow(inboxId, flowId) {
    return axios.delete(`${this.url}/${inboxId}/whatsapp_flows/${flowId}`);
  }

  getWhatsAppFlowAssets(inboxId, flowId) {
    return axios.get(`${this.url}/${inboxId}/whatsapp_flows/${flowId}/assets`);
  }

  getWhatsAppFlowMetrics(inboxId, flowId, params) {
    return axios.get(`${this.url}/${inboxId}/whatsapp_flows/${flowId}/metrics`, {
      params,
    });
  }

  getWhatsAppFlowEncryption(inboxId) {
    return axios.get(`${this.url}/${inboxId}/whatsapp_flows/encryption`);
  }

  updateWhatsAppFlowEncryption(inboxId, businessPublicKey) {
    return axios.post(`${this.url}/${inboxId}/whatsapp_flows/encryption`, {
      business_public_key: businessPublicKey,
    });
  }

  getWhatsAppProfile(inboxId) {
    return axios.get(`${this.url}/${inboxId}/whatsapp_profile`);
  }

  updateWhatsAppProfile(inboxId, profile) {
    return axios.patch(`${this.url}/${inboxId}/whatsapp_profile`, profile);
  }

  getWhatsAppBlockedUsers(inboxId) {
    return axios.get(`${this.url}/${inboxId}/whatsapp_blocked_users`);
  }

  blockWhatsAppUser(inboxId, waId) {
    return axios.post(`${this.url}/${inboxId}/whatsapp_blocked_users`, {
      wa_id: waId,
    });
  }

  unblockWhatsAppUser(inboxId, waId) {
    return axios.delete(`${this.url}/${inboxId}/whatsapp_blocked_users`, {
      data: { wa_id: waId },
    });
  }

  getWhatsAppQrCodes(inboxId) {
    return axios.get(`${this.url}/${inboxId}/whatsapp_qr_codes`);
  }

  createWhatsAppQrCode(inboxId, payload) {
    return axios.post(`${this.url}/${inboxId}/whatsapp_qr_codes`, payload);
  }

  updateWhatsAppQrCode(inboxId, code, payload) {
    return axios.patch(`${this.url}/${inboxId}/whatsapp_qr_codes/${code}`, payload);
  }

  deleteWhatsAppQrCode(inboxId, code) {
    return axios.delete(`${this.url}/${inboxId}/whatsapp_qr_codes/${code}`);
  }

  getWhatsAppAnalytics(inboxId, params) {
    return axios.get(`${this.url}/${inboxId}/whatsapp_analytics`, { params });
  }

  getWhatsAppBilling(inboxId) {
    return axios.get(`${this.url}/${inboxId}/whatsapp_billing`);
  }

  getWhatsAppMedia(inboxId) {
    return axios.get(`${this.url}/${inboxId}/whatsapp_media`);
  }

  deleteWhatsAppMedia(inboxId, mediaId) {
    return axios.delete(`${this.url}/${inboxId}/whatsapp_media/${mediaId}`);
  }

  getWhatsAppPhoneManagement(inboxId) {
    return axios.get(`${this.url}/${inboxId}/whatsapp_phone_management`);
  }

  createCSATTemplate(inboxId, template) {
    return axios.post(`${this.url}/${inboxId}/csat_template`, {
      template,
    });
  }

  getCSATTemplateStatus(inboxId) {
    return axios.get(`${this.url}/${inboxId}/csat_template`);
  }

  analyzeCSATTemplateUtility(inboxId, template) {
    return axios.post(`${this.url}/${inboxId}/csat_template/analyze`, {
      template,
    });
  }

  resetSecret(inboxId) {
    return axios.post(`${this.url}/${inboxId}/reset_secret`);
  }

  rotateHmacToken(inboxId) {
    return axios.post(`${this.url}/${inboxId}/rotate_hmac_token`);
  }

  enableWhatsappCalling(inboxId) {
    return axios.post(`${this.url}/${inboxId}/enable_whatsapp_calling`);
  }

  disableWhatsappCalling(inboxId) {
    return axios.post(`${this.url}/${inboxId}/disable_whatsapp_calling`);
  }

  setInboundCalls(inboxId, enabled) {
    return axios.post(`${this.url}/${inboxId}/set_inbound_calls`, {
      inbound_calls_enabled: enabled,
    });
  }

  setCallRecording(inboxId, { recordingEnabled, transcriptionEnabled }) {
    return axios.post(`${this.url}/${inboxId}/set_call_recording`, {
      recording_enabled: recordingEnabled,
      transcription_enabled: transcriptionEnabled,
    });
  }
}

export default new Inboxes();
